import type { KeyboardEvent } from 'react';

/**
 * The standard keyboard pattern for a group of radio buttons: the group is one
 * Tab stop, the arrow keys move to the next or previous option (wrapping at the
 * ends), and Home and End go to the first and last. By default the option moved
 * to is also chosen; a group can ask for moves that only move focus instead.
 */

/**
 * The option's place in the Tab order: only `stopIndex` is a stop (the first
 * option when it is -1). Pass the checked option's index, or the focused one's
 * while focus moves within a group whose arrows do not choose.
 */
export const radioTabIndex = (index: number, stopIndex: number): 0 | -1 =>
  index === (stopIndex < 0 ? 0 : stopIndex) ? 0 : -1;

const STEP_KEYS: Readonly<Record<string, number>> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

/** The option a key moves to from `index` among `count`, or null for a key the group ignores. */
export const radioTargetIndex = (key: string, index: number, count: number): number | null => {
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  const step = STEP_KEYS[key];
  return step === undefined ? null : (index + step + count) % count;
};

export interface RadioKeyOptions {
  /**
   * True (the default): an arrow, Home or End chooses the option it moves to
   * (selection follows focus). False: those keys move focus only, and Space or
   * Enter (the option's click) chooses; for a group whose choice saves, checks a
   * device or closes a dialog.
   */
  followFocus?: boolean;
  /** False while the group cannot move (a choice is saving): keys are left alone. */
  enabled?: boolean;
}

/**
 * A keydown handler for the option at `index`: moves focus to the target
 * option and reports it through `moveTo`, which is called with whether the
 * move also chooses. The options are the group's `[role="radio"]` elements.
 * A key held with Alt, Ctrl or Meta is left to the browser and the system.
 */
export const onRadioKeyDown =
  (
    index: number,
    count: number,
    moveTo: (index: number, chooses: boolean) => void,
    { followFocus = true, enabled = true }: RadioKeyOptions = {},
  ) =>
  (event: KeyboardEvent<HTMLElement>) => {
    if (!enabled || event.altKey || event.ctrlKey || event.metaKey) return;
    const target = radioTargetIndex(event.key, index, count);
    if (target === null) return;
    event.preventDefault();
    const group = event.currentTarget.closest('[role="radiogroup"]');
    group?.querySelectorAll<HTMLElement>('[role="radio"]')[target]?.focus();
    moveTo(target, followFocus);
  };
